import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search, X } from 'lucide-react';
import { DrawerContent, DrawerRoot, DrawerTitle } from '@components/ui/drawer';
import { cn } from '@utils/cn';
import { MultiSelectSheetProps } from './types';
import { CloseButton } from '@components/ui/closeButton';

const SEARCH_FROM = 8;

export const MultiSelectSheet = ({
  open,
  onOpenChange,
  title,
  options,
  isChecked,
  onToggle,
  singleMode,
  disabled,
  canSelectAll,
  canClear,
  onSelectAll,
  onClear,
  onCreate,
}: MultiSelectSheetProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'forms.default.multiSelect' });
  const { t: tActions } = useTranslation('app', { keyPrefix: 'actions' });
  const [search, setSearch] = useState('');
  const query = search.trim().toLocaleLowerCase();
  const visible = query
    ? options.filter((el) => `${el.label} ${el.sublabel || ''}`.toLocaleLowerCase().includes(query))
    : options;
  const showSearch = options.length >= SEARCH_FROM || Boolean(onCreate);

  const change = (next: boolean) => {
    if (!next) setSearch('');
    onOpenChange(next);
  };

  return (
    <DrawerRoot
      open={open}
      onOpenChange={change}
      direction="bottom"
      nested
      repositionInputs={false}>
      <DrawerContent
        aria-describedby={undefined}
        overlayClassName="z-[80] bg-[rgba(24,28,33,.45)]"
        className="z-[80] h-[85dvh] rounded-t-2xl border-0 pb-[env(safe-area-inset-bottom,0px)] shadow-[0_-8px_30px_rgba(0,0,0,.18)]">
        <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-neutral-200" />
        <div className="flex shrink-0 items-center gap-2 border-b py-1.5 pl-4 pr-1.5">
          <DrawerTitle className="m-0 min-w-0 flex-1 truncate text-base font-bold text-neutral-800">
            {title || t('select')}
          </DrawerTitle>
          {!singleMode && (
            <button
              type="button"
              onClick={() => change(false)}
              className="h-11 px-3 text-sm font-bold text-primary-600">
              {tActions('done')}
            </button>
          )}
          {singleMode && <CloseButton onClick={() => change(false)} />}
        </div>
        {showSearch && (
          <div className="shrink-0 px-4 pb-1 pt-3">
            <label className="flex h-11 items-center gap-2 rounded-md border-[1.5px] border-neutral-300 bg-white px-3 focus-within:border-primary-500">
              <Search className="h-4 w-4 shrink-0 text-neutral-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('search')}
                enterKeyHint="search"
                className="min-w-0 flex-1 bg-transparent text-base text-neutral-800 outline-none placeholder:text-neutral-400"
              />
              {search && (
                <button
                  type="button"
                  aria-label={t('clear')}
                  onClick={() => setSearch('')}
                  className="-mr-1 flex h-8 w-8 items-center justify-center text-neutral-400">
                  <X className="h-4 w-4" />
                </button>
              )}
            </label>
          </div>
        )}
        <div
          role="listbox"
          aria-multiselectable={!singleMode}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-1">
          {visible.map((option) => {
            const checked = isChecked(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={checked}
                disabled={disabled || option.disabled}
                onClick={() => onToggle(option.value)}
                className={cn(
                  'flex min-h-12 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-[15px] text-neutral-800 disabled:opacity-50',
                  checked && 'bg-primary-50 font-semibold text-primary-700',
                )}>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="[overflow-wrap:anywhere]">{option.label}</span>
                  {(option.sublabel || (option.disabled && option.disabledReason)) && (
                    <span className="text-xs font-normal text-neutral-500">
                      {option.disabled ? option.disabledReason : option.sublabel}
                    </span>
                  )}
                </span>
                {checked && (
                  <Check className="h-5 w-5 shrink-0 text-primary-500" strokeWidth={2.6} />
                )}
              </button>
            );
          })}
          {!visible.length && !onCreate && (
            <p className="px-3 py-4 text-sm text-neutral-500">{t('empty')}</p>
          )}
          {onCreate && query && (
            <button
              type="button"
              onClick={() => {
                onCreate(search.trim());
                setSearch('');
              }}
              className="flex min-h-12 w-full items-center gap-2 rounded-lg px-3 text-left text-[15px] font-semibold text-primary-600">
              {t('createNew')}: {search.trim()}
            </button>
          )}
        </div>
        {(!singleMode || canClear) && (
          <div className="flex shrink-0 justify-between border-t px-2 py-1">
            {!singleMode ? (
              <button
                type="button"
                disabled={!canSelectAll}
                onClick={onSelectAll}
                className="h-11 px-3 text-sm font-semibold text-primary-600 disabled:opacity-40">
                {t('selectAll')}
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              disabled={!canClear}
              onClick={onClear}
              className="h-11 px-3 text-sm font-semibold text-primary-600 disabled:opacity-40">
              {t('clear')}
            </button>
          </div>
        )}
      </DrawerContent>
    </DrawerRoot>
  );
};
